// src/hooks/useTemplateRendering.js
import { useState, useEffect, useMemo } from 'react';

export const useTemplateRendering = (templateContent) => {
  const [renderedContent, setRenderedContent] = useState('');
  const [isRendering, setIsRendering] = useState(false);
  const [error, setError] = useState(null);
  const [sampleData, setSampleData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Extract variables from template content
  const variables = useMemo(() => {
    if (!templateContent) return [];
    
    const variableMatches = templateContent.match(/\{\{([^}]+)\}\}/g) || [];
    const uniqueVariables = [...new Set(variableMatches.map(match => match.replace(/[{}]/g, '')))];
    
    return uniqueVariables.map(key => {
      const parts = key.split('.');
      const category = parts[0] || 'General';
      const field = parts[1] || key;
      
      let label = field.replace(/([A-Z])/g, ' $1').trim();
      label = label.charAt(0).toUpperCase() + label.slice(1);
      
      // Determine input type based on field name
      let type = 'text';
      if (field.toLowerCase().includes('email')) type = 'email';
      else if (field.toLowerCase().includes('date')) type = 'date';
      else if (field.toLowerCase().includes('phone') || field.toLowerCase().includes('telefon')) type = 'tel';
      else if (field.toLowerCase().includes('amount') || field.toLowerCase().includes('price') || field.toLowerCase().includes('suma')) type = 'number';
      else if (field.toLowerCase().includes('description') || field.toLowerCase().includes('content') || field.toLowerCase().includes('note')) type = 'textarea';
      
      return {
        key,
        label: `${category} - ${label}`,
        category: category.charAt(0).toUpperCase() + category.slice(1),
        type
      };
    });
  }, [templateContent]);

  // Get unique categories
  const variableCategories = useMemo(() => {
    return [...new Set(variables.map(v => v.category))];
  }, [variables]);

  // Find missing variables (variables in template but not in sample data)
  const missingVariables = useMemo(() => {
    return variables.filter(variable => !sampleData[variable.key] || sampleData[variable.key].trim() === '');
  }, [variables, sampleData]);

  // Template validation
  const isValid = useMemo(() => {
    if (!templateContent) return false;
    
    // Check for basic template structure
    if (templateContent.length < 10) return false;
    
    // Check for unclosed variables
    const openBraces = (templateContent.match(/\{\{/g) || []).length;
    const closeBraces = (templateContent.match(/\}\}/g) || []).length;
    
    return openBraces === closeBraces;
  }, [templateContent]);

  // Initialize sample data with default values
  useEffect(() => {
    const defaultSampleData = {
      'Client.Nume': 'SC Exemplu SRL',
      'Client.CodFiscal': 'RO12345678',
      'Client.Adresa': 'Str. Exemplu nr. 123, București, Sector 1',
      'Client.Email': 'contact@exemplu.ro',
      'Client.Telefon': '0721123456',
      'Client.Reprezentant': 'Ion Popescu',
      'Client.IBAN': 'RO49AAAA1B31007593840000',
      'Client.Banca': 'Banca Transilvania',
      
      'Contract.Numar': 'CNT-2025-0001',
      'Contract.DataInceput': '15.01.2025',
      'Contract.DataExpirare': '31.12.2025',
      'Contract.Servicii': '• Consultanță IT - 150 RON/oră\n• Dezvoltare software - 200 RON/oră\n• Mentenanță sistem - 100 RON/oră',
      'Contract.ValoareTotala': '10,000 RON',
      'Contract.Valuta': 'RON',
      'Contract.TermenPlata': '30 zile',
      
      'Firma.Nume': 'Tech Solutions SRL',
      'Firma.CodFiscal': 'RO87654321',
      'Firma.Adresa': 'Bd. Tehnologiei nr. 456, București, Sector 2',
      'Firma.Email': 'office@techsolutions.ro',
      'Firma.Telefon': '0312345678',
      'Firma.Reprezentant': 'Maria Ionescu',
      'Firma.IBAN': 'RO49BBBB1B31007593840001',
      'Firma.Banca': 'BCR',
      
      'Data.Curenta': new Date().toLocaleDateString('ro-RO'),
      'Data.An': new Date().getFullYear().toString(),
      'Data.Luna': (new Date().getMonth() + 1).toString().padStart(2, '0'),
      'Data.Zi': new Date().getDate().toString().padStart(2, '0')
    };

    // Only set sample data for variables that exist in the template
    const relevantSampleData = {};
    variables.forEach(variable => {
      if (defaultSampleData[variable.key]) {
        relevantSampleData[variable.key] = defaultSampleData[variable.key];
      } else {
        relevantSampleData[variable.key] = `[${variable.label}]`;
      }
    });

    setSampleData(prev => ({...relevantSampleData, ...prev}));
  }, [variables]);

  // Render template with sample data
  useEffect(() => {
    if (!templateContent) {
      setRenderedContent('');
      return;
    }

    setIsRendering(true);
    setError(null);

    try {
      let rendered = templateContent;
      
      // Replace variables with sample data
      Object.entries(sampleData).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`, 'g');
        rendered = rendered.replace(regex, value || `{{${key}}}`);
      });

      // Convert line breaks to HTML
      rendered = rendered.replace(/\n/g, '<br>');
      
      // Add some basic styling for contract elements
      rendered = rendered
        .replace(/CONTRACT DE PRESTĂRI SERVICII/gi, '<div class="contract-header"><h1>CONTRACT DE PRESTĂRI SERVICII</h1></div>')
        .replace(/ARTICOLUL (\d+)/gi, '<h3 style="margin-top: 2rem; margin-bottom: 1rem; color: #1f2937;">ARTICOLUL $1</h3>')
        .replace(/Nr\. ([A-Z0-9-]+)/gi, '<div style="text-align: center; font-weight: bold; margin: 1rem 0;">Nr. $1</div>');

      setRenderedContent(rendered);
    } catch (err) {
      setError(err.message);
      setRenderedContent(templateContent);
    } finally {
      setIsRendering(false);
    }
  }, [templateContent, sampleData]);

  // Update sample data
  const updateSampleData = (key, value) => {
    setSampleData(prev => ({
      ...prev,
      [key]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Reset sample data to defaults
  const resetSampleData = () => {
    setSampleData({});
    setHasUnsavedChanges(false);
  };

  // Export to PDF functionality
  const exportToPdf = async (templateId, filename) => {
    try {
      // If we have a template ID, use the API
      if (templateId) {
        // This would make an API call to your backend to generate PDF
        // For now, we'll simulate this
        console.log('Exporting PDF via API for template:', templateId);
        return { success: true };
      } else {
        // For new templates, we can't use the API, so we'll use the browser's print functionality
        return { success: false, error: 'Nu se poate exporta PDF pentru template-uri nesalvate' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Validate template
  const validateTemplate = () => {
    const issues = [];
    
    if (!templateContent || templateContent.trim().length === 0) {
      issues.push('Template-ul este gol');
    }
    
    if (missingVariables.length > 0) {
      issues.push(`Variabile lipsă: ${missingVariables.map(v => v.key).join(', ')}`);
    }
    
    // Check for unclosed variables
    const openBraces = (templateContent.match(/\{\{/g) || []).length;
    const closeBraces = (templateContent.match(/\}\}/g) || []).length;
    
    if (openBraces !== closeBraces) {
      issues.push('Variabile neînchise corect');
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  };

  return {
    renderedContent,
    isRendering,
    error,
    variables,
    missingVariables,
    isValid,
    sampleData,
    variableCategories,
    updateSampleData,
    resetSampleData,
    exportToPdf,
    validateTemplate,
    hasUnsavedChanges
  };
};